import {
  Box,
  Button,
  ComboboxItem,
  Flex,
  Grid,
  Menu,
  Select,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
  IconCloudCode,
  IconSearch,
  IconShoppingCart,
  IconWorldWww,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useState } from "react";
import { repositoryCart, repositoryWeb } from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";

const LogWeb = () => {
  //#region  table
  const headerRef = React.useRef<HTMLDivElement>(null);
  //data and fetching state
  const [data, setData] = useState<any[]>([]);

  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [typeLog, setTypeLog] = useState<number>(1);

  //table state
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [height, setHeight] = useState(0);
  const [paginationCache, setPaginationCache] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });
  const [paginationCart, setPaginationCart] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [key, setKey] = useDebouncedState<string>("", 500);

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "userId",
        header: "Người thao tác",
      },
      {
        accessorKey: "createDate",
        header: "Ngày tạo",
      },
      {
        accessorKey: "des",
        header: "Mô tả",
      },
      {
        accessorKey: "idCheck",
        header: "Mã nhận dạng",
      },
      {
        accessorKey: "objectSave",
        header: "Dữ liệu",
        Cell: ({ row }) => (
          <Box
            w={550}
            style={{ cursor: "pointer" }}
            onClick={() => modalsCheckLog(row.original.objectSave)}
          >
            <Tooltip label="Xem chi tiết">
              <Text size="15px" fw={"bold"} truncate="end">
                {row.original.objectSave}
              </Text>
            </Tooltip>
          </Box>
        ),
      },
      {
        accessorKey: "typeLog",
        header: "Kiểu Log",
      },
      {
        accessorKey: "ipLog",
        header: "IP",
      },
    ],
    []
  );

  const modalsCheckLog = (dataLog: string) => {
    modals.openConfirmModal({
      title: <Title order={5}>Check Log</Title>,
      size: "auto",
      children: (
        <Box w={"1550px"}>
          <Text
            w={"1550px"}
            style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }}
          >
            {dataLog}
          </Text>
        </Box>
      ),
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `/Cache/get-list?skip=${
        paginationCache.pageIndex * paginationCache.pageSize
      }`;

      if (key?.length > 0) {
        url += `&key=${key?.trim()}`;
      }

      const res = await repositoryWeb.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  const fetchDataCart = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `/Cart/get-list?skip=${
        paginationCart.pageIndex * paginationCart.pageSize
      }`;

      if (key?.length > 0) {
        url += `&key=${key?.trim()}`;
      }

      const res = await repositoryCart.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  useEffect(() => {
    const valuesList = Object.keys(rowSelection);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelection);
      setSelectIds(valuesList);
    }
  }, [rowSelection]);

  useEffect(() => {
    if (typeLog === 1) {
      fetchData();
    } else {
      fetchDataCart();
    }
  }, [
    paginationCache.pageIndex,
    paginationCache.pageSize,
    paginationCart.pageIndex,
    paginationCart.pageSize,
  ]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      // 160 là chiều cao của phần phân trang
      // headerHeight là chiều cao của phần header
      setHeight(window.innerHeight - (130 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize); // Update height on window resize

    return () => {
      window.removeEventListener("resize", handleResize); // Clean up event listener
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    positionToolbarAlertBanner: "bottom",
    enableTopToolbar: false,
    enableRowNumbers: true,
    renderRowActions: () => <></>,
    //add custom action buttons to top-left of top toolbar

    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: false,
    getRowId: (row) => row?.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        // left: ["mrt-row-select"],
        // right: ["action"],
      },
      columnVisibility: { id: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    onRowSelectionChange: setRowSelection,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    rowCount,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: typeLog === 1 ? setPaginationCache : setPaginationCart,
    onSortingChange: setSorting,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        //  fontWeight: row.getIsSelected() ? "bold" : "normal", //conditional styling
        fontWeight: "bold", //conditional styling
        fontSize: "12.5px",
        padding: "14px 15px",
        //  backgroundColor: row.original.userName.length>0 ? "white" : "gray",
      },
    }),
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination: typeLog === 1 ? paginationCache : paginationCart,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      sorting,
      rowSelection,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["5", "15"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
  });
  //#endregion
  return (
    <>
      <div className="mt-5">
        <div ref={headerRef} style={{ width: "100%" }}>
          <Flex
            justify={"space-between"}
            wrap={"wrap"}
            align={"center"}
            w={"100%"}
          >
            <_breadcrumb></_breadcrumb>
          </Flex>
          <Flex w={"100%"} justify={"space-between"} align={"center"}>
            <Grid mt={10} w={"90%"}>
              <Grid.Col span={{ base: 12, xs: 6, md: 6, lg: 2 }}>
                <TextInput
                  placeholder="Nhập từ khóa tìm kiếm"
                  leftSection={<IconSearch color="#15AABF" />}
                  defaultValue={key}
                  onChange={(e) => setKey(e.currentTarget.value)}
                />
              </Grid.Col>
              {/* <Grid.Col span={{ base: 12, xs: 6, md: 6, lg: 2 }}>
              <Select
                placeholder="Loại log web"
                leftSection={<IconCloudCode color="#15AABF" />}
                data={[
                  { label: "Log Web Client", value: "1" },
                  { label: "Log giỏ hàng", value: "2" },
                ]}
                value={typeLog}
                onChange={(e) => setTypeLog(e ? e : "1")}
              />
            </Grid.Col> */}
              <Grid.Col span={{ base: 12, xs: 6, md: 6, lg: 2 }}>
                <Button
                  leftSection={<IconSearch size={14} />}
                  color="blue"
                  variant="outline"
                  onClick={async () => {
                    if (typeLog === 1) {
                      await fetchData();
                    } else {
                      await fetchDataCart();
                    }
                  }}
                >
                  Tìm kiếm
                </Button>
              </Grid.Col>
            </Grid>
            <Menu width={"target"}>
              <Menu.Target>
                <Button leftSection={<IconCloudCode />} w={"250px"}>
                  Loại Log: {typeLog === 1 ? "Log Web Client" : "Log Giỏ Hàng"}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  onClick={() => {
                    fetchData();
                    setTypeLog(1);
                  }}
                  leftSection={<IconWorldWww />}
                >
                  Log Web Client
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    fetchDataCart();
                    setTypeLog(2);
                  }}
                  leftSection={<IconShoppingCart />}
                >
                  Log Giỏ Hàng
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Flex>
        </div>
        <Box mt={10}>
          <MantineReactTable table={table} />
        </Box>
      </div>
    </>
  );
};

export default LogWeb;
